import { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Tag,
  Space,
  Typography,
  theme,
  Tooltip,
  Button,
  Dropdown,
  Avatar,
  Flex,
  Empty,
  Progress,
} from 'antd';
import {
  MailOutlined,
  PhoneOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  RollbackOutlined,
  MoreOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslation } from 'react-i18next';
import type { Payment } from '../api';
import { CheckGatewayStatusModal } from './CheckGatewayStatusModal';
import { RefundPaymentModal } from './RefundPaymentModal';
import { PaymentDetailModal } from './PaymentDetailModal';
import { usePaymentRowActions } from './usePaymentRowActions';
import { formatPaymentTimestamp, getStatusConfig, isPaymentExpired } from './paymentStatus';
import { formatCurrency } from '@/utils/format';
import { getInitials } from '@/utils/userDisplay';
import { ErrorAlert } from '@/components/shared/ErrorAlert';
import { useResponsivePinnedColumn } from '@/hooks/useResponsivePinnedColumn';

dayjs.extend(relativeTime);

const { Text, Paragraph } = Typography;
const { useToken } = theme;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface PaymentsTableProps {
  /** Payment data to display. */
  data: Payment[];
  /** Whether data is currently loading. */
  isLoading: boolean;
  /** Error object if the query failed. */
  error: Error | null;
  /** Callback to manually refresh data (e.g. after a refund). */
  onRefresh: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Table displaying all payment records with inline status, customer info,
 * gateway badges, and a timeline column with expiry progress bars.
 *
 * Each row has an "Actions" dropdown (see usePaymentRowActions) providing:
 * - View full payment details (modal)
 * - Check gateway status (modal)
 * - Copy payment URL
 * - Process refund (modal, only for completed payments)
 * - Delete payment link (only when policy.canBeDeleted)
 */
export function PaymentsTable({
  data,
  isLoading,
  error,
  onRefresh,
}: PaymentsTableProps) {
  const { token } = useToken();
  const { t } = useTranslation('payment-module');
  const {
    getActionMenuItems,
    copiedPaymentUrl,
    copyPaymentUrl,
    detailPayment,
    closeDetails,
    gatewayStatusPayment,
    closeGatewayStatus,
    refundPayment,
    closeRefund,
  } = usePaymentRowActions(onRefresh);

  // -- Pagination --
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  /** Keep the current page in range when the dataset shrinks (delete, filters). */
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(data.length / pageSize));
    setCurrentPage((page) => (page > maxPage ? maxPage : page));
  }, [data.length, pageSize]);

  // -------------------------------------------------------------------------
  // Memoised table columns
  // -------------------------------------------------------------------------

  const pinColumn = useResponsivePinnedColumn();
  const columns: ColumnsType<Payment> = useMemo(
    () => [
      // -- Payment summary (amount, description, request ID, status tag) --
      {
        title: t('table.payment'),
        key: 'payment',
        width: 320,
        fixed: pinColumn('left'),
        render: (_: unknown, record: Payment) => {
          const statusConfig = getStatusConfig(record.status, t);
          return (
            <Flex gap={token.marginSM} align="center">
              <Avatar
                size={44}
                style={{
                  backgroundColor: token[statusConfig.bgToken],
                  color: token.colorText,
                  flexShrink: 0,
                }}
                icon={<CreditCardOutlined />}
              />
              <div style={{ minWidth: 0, flex: 1 }}>
                <Text strong style={{ fontSize: token.fontSizeLG, display: 'block' }}>
                  {formatCurrency(record.amount, record.currency)}
                </Text>
                {record.status.toLowerCase() === 'refunded' && record.refundedAmount != null && (
                  <Text type="warning" style={{ fontSize: token.fontSizeSM, display: 'block' }}>
                    <RollbackOutlined /> {t('status.refunded')}: {formatCurrency(record.refundedAmount, record.currency)}
                  </Text>
                )}
                <Paragraph
                  type="secondary"
                  ellipsis={{ rows: 1 }}
                  style={{ margin: 0, fontSize: token.fontSizeSM }}
                >
                  {record.description || t('table.noDescription')}
                </Paragraph>
                <Tag color="geekblue" style={{ margin: 0, marginTop: 4 }}>
                  {record.requestId}
                </Tag>
              </div>
              <Tag
                color={statusConfig.color}
                icon={statusConfig.icon}
                style={{ margin: 0, flexShrink: 0 }}
              >
                {statusConfig.label}
              </Tag>
            </Flex>
          );
        },
        sorter: (a: Payment, b: Payment) => a.amount - b.amount,
      },

      // -- Customer (avatar with initials, name, email, phone) --
      {
        title: t('table.customer'),
        key: 'customer',
        width: 240,
        render: (_: unknown, record: Payment) => (
          <Flex gap={token.marginSM} align="center">
            <Avatar
              size={40}
              style={{
                backgroundColor: token.colorPrimaryBg,
                color: token.colorPrimary,
                flexShrink: 0,
              }}
            >
              {getInitials(record.customerName)}
            </Avatar>
            <div style={{ minWidth: 0 }}>
              <Text strong ellipsis style={{ display: 'block' }}>
                {record.customerName}
              </Text>
              <Tooltip title={record.customerEmail}>
                <Text
                  type="secondary"
                  style={{ fontSize: token.fontSizeSM, display: 'block' }}
                  ellipsis
                >
                  <MailOutlined style={{ marginRight: 4 }} />
                  {record.customerEmail}
                </Text>
              </Tooltip>
              {record.customerPhone && (
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  <PhoneOutlined style={{ marginRight: 4 }} />
                  {record.customerPhone}
                </Text>
              )}
            </div>
          </Flex>
        ),
        sorter: (a: Payment, b: Payment) => a.customerName.localeCompare(b.customerName),
      },

      // -- Gateway provider and locale --
      {
        title: t('table.gateway'),
        key: 'gateway',
        width: 120,
        render: (_: unknown, record: Payment) => (
          <Flex vertical gap={4}>
            <Tag color="purple" style={{ margin: 0, width: 'fit-content' }}>
              {record.gateway.toUpperCase()}
            </Tag>
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              <GlobalOutlined style={{ marginRight: 4 }} />
              {record.locale.toUpperCase()}
            </Text>
          </Flex>
        ),
        filters: [
          { text: 'Borica', value: 'borica' },
          { text: 'Stripe', value: 'stripe' },
          { text: 'PayPal', value: 'paypal' },
        ],
        onFilter: (value, record) => record.gateway.toLowerCase() === value,
      },

      // -- Timeline (created date, expiry progress for pending payments) --
      {
        title: t('table.timeline'),
        key: 'timeline',
        width: 200,
        render: (_: unknown, record: Payment) => {
          const createdDate = dayjs.unix(record.createdAt);
          const expiresDate = dayjs.unix(record.expiresAt);
          const expired = isPaymentExpired(record.expiresAt);
          const now = dayjs();

          // Progress percentage for pending payments (0–100)
          const totalDuration = expiresDate.diff(createdDate);
          const elapsed = now.diff(createdDate);
          const progress = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));

          return (
            <Flex vertical gap={4}>
              <Text style={{ fontSize: token.fontSizeSM }}>
                <CalendarOutlined style={{ marginRight: 4, color: token.colorTextSecondary }} />
                {formatPaymentTimestamp(record.createdAt)}
              </Text>
              {record.status.toLowerCase() === 'pending' && (
                <>
                  <Progress
                    percent={progress}
                    size="small"
                    status={expired ? 'exception' : 'active'}
                    showInfo={false}
                  />
                  <Text
                    type={expired ? 'danger' : 'secondary'}
                    style={{ fontSize: token.fontSizeSM }}
                  >
                    {expired ? t('table.expired') : t('table.expires', { time: expiresDate.fromNow() })}
                  </Text>
                </>
              )}
              {record.status.toLowerCase() !== 'pending' && (
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {createdDate.fromNow()}
                </Text>
              )}
            </Flex>
          );
        },
        sorter: (a: Payment, b: Payment) => a.createdAt - b.createdAt,
        defaultSortOrder: 'descend' as const,
      },

      // -- Actions dropdown --
      {
        title: '',
        key: 'actions',
        width: 110,
        fixed: pinColumn('right'),
        align: 'center' as const,
        render: (_: unknown, record: Payment) => (
          <Dropdown
            menu={{ items: getActionMenuItems(record) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button icon={<MoreOutlined />}>
              {t('actions.actions')}
            </Button>
          </Dropdown>
        ),
      },
    ],
    [token, copiedPaymentUrl, getActionMenuItems, t, pinColumn],
  );

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div>
      {/* Query error banner */}
      {error && (
        <ErrorAlert
          title={t('actions.errorLoading')}
          error={error}
          fallbackDescription={t('errors.unknown')}
          style={{ margin: token.marginMD }}
        />
      )}

      {/* Main payments table */}
      <Table
        columns={columns}
        dataSource={data}
        rowKey={(record) => record.id.toString()}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize,
          total: data.length,
          showQuickJumper: data.length > 50,
          showTotal: (total, range) =>
            t('table.paginationTotal', { start: range[0], end: range[1], total }),
          onChange: (page, size) => {
            setCurrentPage(page);
            if (size !== pageSize) {
              setPageSize(size);
              setCurrentPage(1);
            }
          },
          style: {
            marginRight: token.marginMD,
            marginBottom: token.marginMD,
            marginTop: token.marginMD,
          },
        }}
        scroll={{ x: 'max-content' }}
        size="middle"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space orientation="vertical" size={4}>
                  <Text type="secondary">{t('table.noPayments')}</Text>
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    {t('table.createToStart')}
                  </Text>
                </Space>
              }
            />
          ),
        }}
        rowClassName={(record) =>
          record.status.toLowerCase() === 'pending' && isPaymentExpired(record.expiresAt)
            ? 'ant-table-row-expired'
            : ''
        }
      />

      <PaymentDetailModal
        payment={detailPayment}
        onClose={closeDetails}
        copiedPaymentUrl={copiedPaymentUrl}
        onCopyUrl={copyPaymentUrl}
      />

      {/* ----------------------------------------------------------------- */}
      {/* Gateway status modal                                              */}
      {/* ----------------------------------------------------------------- */}
      <CheckGatewayStatusModal
        open={gatewayStatusPayment != null}
        onClose={closeGatewayStatus}
        payment={gatewayStatusPayment}
      />

      {/* ----------------------------------------------------------------- */}
      {/* Refund payment modal                                              */}
      {/* ----------------------------------------------------------------- */}
      <RefundPaymentModal
        open={refundPayment != null}
        onClose={closeRefund}
        payment={refundPayment}
        onSuccess={onRefresh}
      />
    </div>
  );
}
